import classNames from 'classnames';
import Link from 'next/link';
import React, {ComponentProps} from 'react';

import {
  ExpertIcon,
  FeedbackIcon,
  HelpIcon,
  LeaderboardIcon,
  MoneyIcon,
  NeighbourIcon,
  ToolsIcon,
} from '../../../../resources/Icons';
import Col from '../../../common/Col';
import {useEnvironment} from '../../../common/EnvironmentProvider';
import Row from '../../../common/Row';
import {useBreakpoint} from '../../../common/util/useBreakpoint';
import {formatMoney} from '../../mechanics';
import NormalButton from './NormalButton';

type CommonProps = Omit<ComponentProps<typeof NormalButton>, 'left' | 'right'>;

type ExpertProps = CommonProps & {
  cost: number;
};

type BuyProps = CommonProps & {
  purchased: boolean;
  cost: number;
};

export const PillButton = ({
  icon,
  style,
  ...props
}: CommonProps & {
  icon: React.ReactNode;
}) => {
  return (
    <NormalButton
      left={<div className="flex ml-2 my-0.5 h-[4.5rem] w-[4.5rem] align-center items-center">{icon}</div>}
      style={{...style, borderRadius: '8rem'}}
      {...props}
    />
  );
};

export const NeighbourButton = (props: CommonProps) => (
  <PillButton
    icon={<NeighbourIcon className="h-[4.5rem] w-[4.5rem] mobile:h-14 mobile:w-14 mobile:mx-auto" />}
    {...props}
  >
    <Col className="text-xs font-medium gap-1 whitespace-nowrap">
      <Row>Talk to a</Row>
      <Row>neighbour</Row>
    </Col>
  </PillButton>
);

export const ExpertButton = ({cost, ...props}: ExpertProps) => (
  <PillButton icon={<ExpertIcon className="h-[4.5rem] w-[4.5rem] mobile:h-14 mobile:w-14 mobile:mx-auto" />} {...props}>
    <Col className="font-medium gap-1 whitespace-nowrap">
      <div className="text-xs">Talk to an expert</div>
      <div className="mobile:text-lg text-xl">{formatMoney(cost)} fee</div>
    </Col>
  </PillButton>
);

export const HelpButton = (props: CommonProps) => (
  <PillButton icon={<HelpIcon className="mobile:h-14 mobile:w-14 mobile:mx-auto" />} {...props}>
    <Col className="text-xs font-medium gap-1 whitespace-nowrap">
      <div>Find out</div>
      <div>yourself</div>
    </Col>
  </PillButton>
);

export const ToolsButton = (props: CommonProps) => {
  const {isMobile} = useBreakpoint('mobile');
  if (isMobile) {
    return (
      <NormalButton className="!p-4" {...props}>
        <ToolsIcon className="h-14 w-14" />
      </NormalButton>
    );
  }
  return (
    <PillButton icon={<ToolsIcon />} {...props}>
      <div className={'text-base'}>Buy tools</div>
    </PillButton>
  );
};

export const FeedbackButton = ({textOnly, ...props}: CommonProps & {textOnly?: boolean}) => {
  const environment = useEnvironment();
  const {isMobile} = useBreakpoint('mobile');
  const url = environment?.game.feedbackFormUrl ?? '';

  return (
    <Link id="provideFeedback" href={url} target="_blank" rel="noopener noreferrer">
      {textOnly ? (
        <NormalButton tabIndex={-1} {...props}>
          Provide game feedback
        </NormalButton>
      ) : isMobile ? (
        <NormalButton tabIndex={-1} {...props} className={classNames(props.className, '!p-4')}>
          <FeedbackIcon className="h-14 w-14" />
        </NormalButton>
      ) : (
        <PillButton
          tabIndex={-1}
          className="!text-base max-w-[20rem]"
          icon={<FeedbackIcon className="h-14 w-14" />}
          {...props}
        >
          <div className="leading-9">Provide feedback</div>
        </PillButton>
      )}
    </Link>
  );
};

export const LeaderboardButton = (props: CommonProps) => (
  <PillButton icon={<LeaderboardIcon className="mobile:h-14 mobile:w-14 mobile:mx-auto" />} {...props}>
    <Col className="!text-base font-medium gap-1 whitespace-nowrap">
      <div>Leader</div>
      <div>board</div>
    </Col>
  </PillButton>
);

export const BuyButton = ({cost, purchased, ...props}: BuyProps) => (
  <PillButton icon={<MoneyIcon className="h-[45px] w-[45px]" />} {...props}>
    {!purchased ? (
      <Col className="text-base">
        <Row className="text-xs">Buy for</Row>
        <Row className="text-xl">{formatMoney(cost)}</Row>
      </Col>
    ) : (
      <Col className="text-base">
        <Row className="text-xl text-red-200">{formatMoney(-cost)}</Row>
        <Row className="text-xs">Purchased</Row>
      </Col>
    )}
  </PillButton>
);

export const PayButton = ({cost, ...props}: BuyProps) => (
  <PillButton icon={<MoneyIcon className="h-[45px] w-[45px]" />} {...props}>
    <Col className="text-base font-medium">
      <Row className="text-xs">Pay</Row>
      <Row className="text-xl">{formatMoney(cost)}</Row>
    </Col>
  </PillButton>
);
